import { useCallback, useMemo, useState } from 'react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import cx from 'classnames';
import { DropdownMenu } from '../../common/DropdownMenu';
import { Toggle } from '../../common/Toggle';
import { CreateAdExternalListingInput, CreateExternalListingModal_CreateExternalListingMutation, CreateExternalListingModal_CreateExternalListingMutationVariables, CreateExternalListingModal_GetShopsQuery, CreateExternalListingModal_GetShopsQueryVariables, ListingsSection_AdFragment, ListingsSection_SyncStockMutation, ListingsSection_SyncStockMutationVariables, ListingsSection_UpdateExternalListingMutation, ListingsSection_UpdateExternalListingMutationVariables } from '../../../../codegen/graphql';
import type { ExternalListingRow_ExternalListingFragment, ListingsSection_SyncExternalListingMutation, ListingsSection_SyncExternalListingMutationVariables, ListingsSection_SyncPricesAliexpressMutation, ListingsSection_SyncPricesAliexpressMutationVariables } from '../../../../codegen/graphql';
import { syncToLeboncoin } from '../../../lib/leboncoin/leboncoin_syncAd';
import { useRequestManager } from '../../common/RequestManager';
import { getPlatformColor } from '../../../utils/getPlatformColor';
import { CreateExternalListingModal } from './CreateExternalListingModal';
import { CREATE_EXTERNAL_LISTING_MUTATION, GET_SHOPS_QUERY, LANGS_BY_SHOP_TYPE } from './CreateExternalListingModal';
import { EXTERNAL_LISTING_ROW_FRAGMENT, ExternalListingRow } from './ExternalListingRow';
import { BulkEditListingMarginModal } from './BulkEditListingMarginModal';

const EXTERNAL_LISTING_FRAGMENT = gql`
  fragment ListingsSection_ExternalListing on AdExternalListing {
    id
    adId
    shopId
    lang
    externalId
    externalData
    shop {
      id
      title
      shopType
      shopKey
    }
    configuration {
      isMultiVariation
      marginPercentOnBaseCost
      targetListingPriceIncludingVat
      listingPriceRoundingMethod
      supplierKey
      useSingleVariationId
      variationsConfig {
        types
        values
        variations
      }
    }
    lastSyncAt
    ...ExternalListingRow_ExternalListing
  }
  ${EXTERNAL_LISTING_ROW_FRAGMENT}
`;

export const LISTINGS_SECTION_FRAGMENT = gql`
  fragment ListingsSection_ad on Ad {
    id
    title
    ebayUrl
    typeIdUsedForImages
    marginPercentOnBaseCost
    targetListingPriceIncludingVat
    listingPriceRoundingMethod
    externalListings {
      ...ListingsSection_ExternalListing
    }

    variationTypes {
      id
      type
      typeValues {
        id
        value
        medias {
          id
          url
        }
      }
    }

    variations {
      id
      marginPercentOnBaseCost
      targetListingPriceIncludingVat
      listingPriceRoundingMethod
      supplierProductVariations {
        sku
        supplierName
        calculatedCost {
          destinationCountry
          totalCostIncludingVAT
          totalCostExcludingVAT
          totalCostVAT
          VATRate
        }
        availableStock {
          destinationCountry
          availableStock
        }
        packaging
        data
      }
      variationTypeValues {
        id
        value
        type {
          id
          type
        }
        medias {
          id
          url
        }
      }
    }
  }
  ${EXTERNAL_LISTING_FRAGMENT}
`;

export const DELETE_EXTERNAL_LISTING_MUTATION = gql`
  mutation ListingsSection_DeleteExternalListing($id: ID!) {
    deleteAdExternalListing(id: $id)
  }
`;

export const UPDATE_EXTERNAL_LISTING_MUTATION = gql`
  mutation ListingsSection_UpdateExternalListing($id: ID!, $input: UpdateAdExternalListingInput!) {
    updateAdExternalListing(id: $id, input: $input) {
      ...ListingsSection_ExternalListing
    }
  }
  ${EXTERNAL_LISTING_FRAGMENT}
`;

const SYNC_STOCK_MUTATION = gql`
  mutation ListingsSection_SyncStock($adId: ID!) {
    syncStock(adId: $adId) {
      id
    }
  }
`;

const SYNC_PRICES_ALIEXPRESS_MUTATION = gql`
  mutation ListingsSection_SyncPricesAliexpress($adId: ID!) {
    syncPricesAliexpress(adId: $adId) {
      id
    }
  }
`;

const SYNC_EXTERNAL_LISTING_MUTATION = gql`
  mutation ListingsSection_SyncExternalListing($adExternalListingId: ID!) {
    syncExternalListing(adExternalListingId: $adExternalListingId) {
      process {
        id
      }
    }
  }
`;

interface ListingsSectionProps {
  adData: ListingsSection_AdFragment;
  refetch: () => void;
}

export function ListingsSection({
  adData,
  refetch,
}: ListingsSectionProps) {
  const { trackRequest } = useRequestManager();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isBulkMarginModalOpen, setIsBulkMarginModalOpen] = useState(false);
  const [selectedListingIds, setSelectedListingIds] = useState<Set<string>>(() => new Set());
  const [shopTypeFilter, setShopTypeFilter] = useState<string | null>(null);
  const [langFilter, setLangFilter] = useState<string | null>(null);
  const [collapsedShopIds, setCollapsedShopIds] = useState<Set<string>>(() => new Set());
  const [copiedListingConfiguration, setCopiedListingConfiguration] = useState<NonNullable<ExternalListingRow_ExternalListingFragment['configuration']> | null>(null);
  const [copiedFromListingId, setCopiedFromListingId] = useState<string | null>(null);
  const [syncStock] = useMutation<
    ListingsSection_SyncStockMutation,
    ListingsSection_SyncStockMutationVariables
  >(SYNC_STOCK_MUTATION);
  const [updateExternalListing] = useMutation<
    ListingsSection_UpdateExternalListingMutation,
    ListingsSection_UpdateExternalListingMutationVariables
  >(UPDATE_EXTERNAL_LISTING_MUTATION);

  const [syncExternalListing] = useMutation<
    ListingsSection_SyncExternalListingMutation,
    ListingsSection_SyncExternalListingMutationVariables
  >(SYNC_EXTERNAL_LISTING_MUTATION);

  const [getShops] = useLazyQuery<
    CreateExternalListingModal_GetShopsQuery,
    CreateExternalListingModal_GetShopsQueryVariables
  >(GET_SHOPS_QUERY);

  const [createExternalListing] = useMutation<
    CreateExternalListingModal_CreateExternalListingMutation,
    CreateExternalListingModal_CreateExternalListingMutationVariables
  >(CREATE_EXTERNAL_LISTING_MUTATION);

  const { id: adId, externalListings } = adData;

  const [syncPricesAliexpress] = useMutation<
    ListingsSection_SyncPricesAliexpressMutation,
    ListingsSection_SyncPricesAliexpressMutationVariables
  >(SYNC_PRICES_ALIEXPRESS_MUTATION);

  const syncStockLeboncoin = useCallback(async () => {
    await trackRequest(
      syncToLeboncoin(adId),
      {
        title: 'Syncing stock to Leboncoin',
        isBlocking: true,
      }
    );
  }, [adId, trackRequest]);

  const handleCopyConfiguration = useCallback((
    config: NonNullable<ExternalListingRow_ExternalListingFragment['configuration']>,
    fromListingId: string
  ) => {
    setCopiedListingConfiguration(config);
    setCopiedFromListingId(fromListingId);
  }, []);

  const handlePasteConfiguration = useCallback(async (listingId: string) => {
    if (!copiedListingConfiguration) return;
    await trackRequest(
      updateExternalListing({
        variables: {
          id: listingId,
          input: { configuration: copiedListingConfiguration },
        },
      }),
      {
        title: 'Pasting listing configuration',
        isBlocking: false,
      }
    );
    refetch();
  }, [copiedListingConfiguration, updateExternalListing, trackRequest, refetch]);

  const syncAllAliexpressPrices = useCallback(async () => {
    await trackRequest(
      syncPricesAliexpress({ variables: { adId } }),
      {
        title: 'Syncing all Aliexpress prices',
        isBlocking: true,
      }
    );
  }, [adId, syncPricesAliexpress, trackRequest]);

  const syncAllTemuExternalListings = useCallback(async () => {
    await trackRequest(
      Promise.all(externalListings.filter((listing) => listing.shop.shopType === 'temu').map((listing) => {
        return syncExternalListing({ variables: { adExternalListingId: listing.id } });
      })),
      {
        title: 'Syncing all Temu external listings',
        isBlocking: true,
      }
    );
  }, [externalListings, syncExternalListing, trackRequest]);

  const createMissingListingsForShopType = useCallback(async (shopType: 'temu' | 'aliexpress') => {
    const platformLabel = shopType === 'temu' ? 'Temu' : 'Aliexpress';
    const { data: shopsData } = await getShops();
    const shops = (shopsData?.shops ?? []).filter((shop) => shop.shopType === shopType);
    const langs = LANGS_BY_SHOP_TYPE[shopType] ?? [];

    if (shops.length === 0) {
      toast.warn(`No ${platformLabel} shops found`);
      return;
    }

    const usedShopLangCombos = new Set(
      externalListings.map((listing) => `${listing.shopId}:${listing.lang.toUpperCase()}`),
    );

    const listingsToCreate: CreateAdExternalListingInput[] = [];
    for (const shop of shops) {
      for (const langOption of langs) {
        if (!usedShopLangCombos.has(`${shop.id}:${langOption.identifier}`)) {
          listingsToCreate.push({
            adId,
            shopId: shop.id,
            lang: langOption.identifier,
          });
        }
      }
    }

    if (listingsToCreate.length === 0) {
      toast.info(`All ${platformLabel} listings already exist`);
      return;
    }

    await trackRequest(
      Promise.all(listingsToCreate.map((input) => createExternalListing({ variables: { input } }))),
      {
        title: `Creating ${listingsToCreate.length} missing ${platformLabel} listing(s)`,
        isBlocking: true,
      },
    );

    toast.success(`Created ${listingsToCreate.length} missing ${platformLabel} listing(s)`);
    refetch();
  }, [adId, createExternalListing, externalListings, getShops, refetch, trackRequest]);

  const uniqueShopTypes = useMemo(() => {
    return [...new Set(externalListings.map((listing) => listing.shop.shopType))].sort();
  }, [externalListings]);

  const uniqueLangs = useMemo(() => {
    return [...new Set(externalListings.map((listing) => listing.lang))].sort();
  }, [externalListings]);

  const filteredListings = useMemo(() => {
    return externalListings.filter((listing) => {
      if (shopTypeFilter && listing.shop.shopType !== shopTypeFilter) {
        return false;
      }
      if (langFilter && listing.lang !== langFilter) {
        return false;
      }
      return true;
    });
  }, [externalListings, shopTypeFilter, langFilter]);

  const listingsByShop = useMemo(() => {
    const groups = new Map<string, {
      shop: (typeof filteredListings)[number]['shop'];
      listings: typeof filteredListings;
    }>();

    for (const listing of filteredListings) {
      const existing = groups.get(listing.shopId);
      if (existing) {
        existing.listings.push(listing);
      } else {
        groups.set(listing.shopId, {
          shop: listing.shop,
          listings: [listing],
        });
      }
    }

    return [...groups.values()].sort((a, b) => {
      const titleCompare = a.shop.title.localeCompare(b.shop.title);
      if (titleCompare !== 0) return titleCompare;
      return a.shop.shopType.localeCompare(b.shop.shopType);
    });
  }, [filteredListings]);

  const selectedListings = useMemo(() => {
    return externalListings.filter((listing) => selectedListingIds.has(listing.id));
  }, [externalListings, selectedListingIds]);

  const allFilteredSelected = filteredListings.length > 0
    && filteredListings.every((listing) => selectedListingIds.has(listing.id));

  const toggleListingSelected = useCallback((listingId: string, selected: boolean) => {
    setSelectedListingIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(listingId);
      } else {
        next.delete(listingId);
      }
      return next;
    });
  }, []);

  const toggleSelectAllFiltered = useCallback((selected: boolean) => {
    if (selected) {
      setSelectedListingIds(new Set(filteredListings.map((listing) => listing.id)));
    } else {
      setSelectedListingIds(new Set());
    }
  }, [filteredListings]);

  const toggleShopCollapsed = useCallback((shopId: string) => {
    setCollapsedShopIds((prev) => {
      const next = new Set(prev);
      if (next.has(shopId)) {
        next.delete(shopId);
      } else {
        next.add(shopId);
      }
      return next;
    });
  }, []);

  const toggleSelectShopListings = useCallback((listingIds: string[], selected: boolean) => {
    setSelectedListingIds((prev) => {
      const next = new Set(prev);
      for (const listingId of listingIds) {
        if (selected) {
          next.add(listingId);
        } else {
          next.delete(listingId);
        }
      }
      return next;
    });
  }, []);

  const handleBulkEditMargin = useCallback(async (update: {
    marginPercentOnBaseCost?: number | null;
    targetListingPriceIncludingVat?: number | null;
    listingPriceRoundingMethod?: import('../../../../codegen/graphql').PriceRoundingMethod | null;
  }) => {
    await trackRequest(
      Promise.all(selectedListings.map((listing) => {
        return updateExternalListing({
          variables: {
            id: listing.id,
            input: {
              configuration: {
                ...listing.configuration,
                ...update,
              },
            },
          },
        });
      })),
      {
        title: `Updating margin for ${selectedListings.length} listing(s)`,
        isBlocking: true,
      }
    );
    refetch();
    setSelectedListingIds(new Set());
  }, [selectedListings, updateExternalListing, trackRequest, refetch]);

  return (
    <div className="space-y-6">
      <div className="flex flex-row gap-4 flex-wrap items-center">
        <DropdownMenu
          label="Actions"
          menuPlacement="left"
          actions={[
            {
              label: 'Add External Listing',
              onClick: () => setIsCreateModalOpen(true),
            },
            {
              label: 'Create missing Temu listings',
              onClick: () => createMissingListingsForShopType('temu'),
            },
            {
              label: 'Create missing Aliexpress listings',
              onClick: () => createMissingListingsForShopType('aliexpress'),
            },
            {
              label: 'Sync All Aliexpress Prices',
              onClick: () => syncAllAliexpressPrices(),
            },
            {
              label: 'Sync All Temu Prices',
              onClick: () => syncAllTemuExternalListings(),
            },
            {
              label: 'Sync Stock',
              onClick: () => {
                trackRequest(
                  syncStock({ variables: { adId } }),
                  {
                    title: 'Syncing stock',
                    isBlocking: true,
                  }
                );
              },
            },
            {
              label: 'Sync Stock Leboncoin',
              onClick: () => {
                trackRequest(
                  syncStockLeboncoin(),
                  {
                    title: 'Syncing stock to Leboncoin',
                    isBlocking: true,
                  }
                );
              },
            },
            {
              label: 'Edit margin percentage',
              onClick: () => {
                if (selectedListingIds.size === 0) {
                  toast.warn('Please select at least one listing');
                  return;
                }
                setIsBulkMarginModalOpen(true);
              },
            },
          ]}
        />

        <select
          className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 bg-white"
          value={shopTypeFilter ?? ''}
          onChange={(e) => setShopTypeFilter(e.target.value || null)}
        >
          <option value="">All shop types</option>
          {uniqueShopTypes.map((shopType) => (
            <option key={shopType} value={shopType}>{shopType}</option>
          ))}
        </select>

        <select
          className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 bg-white"
          value={langFilter ?? ''}
          onChange={(e) => setLangFilter(e.target.value || null)}
        >
          <option value="">All languages</option>
          {uniqueLangs.map((lang) => (
            <option key={lang} value={lang}>{lang}</option>
          ))}
        </select>

        {selectedListingIds.size > 0 && (
          <span className="text-sm text-gray-600">
            {selectedListingIds.size} listing(s) selected
          </span>
        )}

        <div className="flex items-center gap-2 ml-auto">
          <Toggle
            label="Select all"
            value={allFilteredSelected}
            onChange={toggleSelectAllFiltered}
            disabled={filteredListings.length === 0}
          />
        </div>
      </div>

      {listingsByShop.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm px-4 py-8 text-center text-gray-500">
          {externalListings.length === 0
            ? 'No external listings yet. Add one to get started.'
            : 'No listings match the current filters.'}
        </div>
      ) : (
        <div className="space-y-3">
          {listingsByShop.map(({ shop, listings }) => {
            const isExpanded = !collapsedShopIds.has(shop.id);
            const shopListingIds = listings.map((listing) => listing.id);
            const allShopSelected = listings.every((listing) => selectedListingIds.has(listing.id));

            return (
              <div key={shop.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => toggleShopCollapsed(shop.id)}
                    className="flex items-center gap-2 flex-1 min-w-0 text-left hover:opacity-80"
                  >
                    <span className="text-gray-500 shrink-0">
                      {isExpanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-gray-900 truncate">
                          {shop.title}
                        </span>
                        <span
                          className={cx(
                            'text-xs font-semibold',
                            getPlatformColor(shop.shopType).textClass
                          )}
                        >
                          {shop.shopType}
                          {shop.shopKey ? ` · ${shop.shopKey}` : ''}
                        </span>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 shrink-0 ml-auto">
                      {listings.length} listing{listings.length === 1 ? '' : 's'}
                    </span>
                  </button>
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="shrink-0"
                  >
                    <Toggle
                      label=""
                      value={allShopSelected}
                      onChange={(selected) => toggleSelectShopListings(shopListingIds, selected)}
                    />
                  </div>
                </div>

                {isExpanded && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12" />
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Shop
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Lang
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            External ID
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Sync
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Supplier
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Variations
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Prices
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Location
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {listings.map((listing) => (
                          <ExternalListingRow
                            key={listing.id}
                            externalListing={listing}
                            adData={adData}
                            onDeleted={() => {
                              refetch();
                            }}
                            copiedListingConfiguration={copiedListingConfiguration}
                            copiedFromListingId={copiedFromListingId}
                            onCopyConfiguration={handleCopyConfiguration}
                            onPasteConfiguration={handlePasteConfiguration}
                            isSelected={selectedListingIds.has(listing.id)}
                            onToggleSelect={toggleListingSelected}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <CreateExternalListingModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          refetch();
          setIsCreateModalOpen(false);
        }}
        adData={adData}
      />

      {isBulkMarginModalOpen && selectedListings.length > 0 && (
        <BulkEditListingMarginModal
          adData={adData}
          selectedListings={selectedListings}
          onClose={() => setIsBulkMarginModalOpen(false)}
          onApply={handleBulkEditMargin}
        />
      )}
    </div>
  );
}
